app.service('recordingUploadService', ['$http', 'HttpAuth', 'apiHelper', 'portalHelper', 'CONSTANTS',
  function($http, HttpAuth, apiHelper, portalHelper, CONSTANTS) {

    const API_URL = apiHelper.getApiUrl();
    const CHUNK_SIZE = 10 * 1024 * 1024; 
    const MAX_RECORDING_FILE_SIZE = CONSTANTS.MAX_RECORDING_FILE_SIZE_BYTES; 
    const SUPPORTED_EXTENSIONS = ['mp3', 'mp4', 'wav', 'm4a'];
    const SUPPORTED_MIME_TYPES = ['audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/x-m4a', 'video/mp4'];
    const SUPPORTED_FORMATS = SUPPORTED_MIME_TYPES; 

    const validateFile = (file) => {
      const errors = [];

      if (!file) {
        errors.push('No file selected');
        return errors;
      }

      const isValidMimeType = SUPPORTED_MIME_TYPES.some(format => file.type === format);

      if (!isValidMimeType) {
        errors.push('Supported formats: MP3, MP4, WAV, M4A');
      }

      if (file.size > MAX_RECORDING_FILE_SIZE) {
        errors.push('File size must be less than 2GB');
      }

      const fileExtension = file.name.split('.').pop().toLowerCase();
      const isValidFormat = SUPPORTED_MIME_TYPES.some(format => file.type === format) || SUPPORTED_EXTENSIONS.includes(fileExtension);

      if (!isValidFormat) {
        errors.push('Supported formats: ' + SUPPORTED_EXTENSIONS.map(ext => ext.toUpperCase()).join(', '));
      }

      return errors;
    };


    const initiateMultipartUpload = (fusionAssignmentId, file) => {
      const url = `${API_URL}/api/admin/fusion_assignment/${fusionAssignmentId}/recordings/initiate-recording-upload/`;
      const totalParts = Math.ceil(file.size / CHUNK_SIZE);

      const payload = {
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        parts: totalParts
      };

      return HttpAuth.post(url, payload);
    };

    const uploadChunk = (presignedUrl, chunk) => {
      return $http.put(presignedUrl, chunk, {
        headers: {
          'Content-Type': 'application/octet-stream'
        },
        withCredentials: false  
      });
    };

    function getChunkUploadPromises(presignedUrls, file, uploadedParts, progressCallback) {
      const chunkPromises = [];
      for (let i = 0; i < presignedUrls.length; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const presignedUrl = presignedUrls[i].url;

        const chunkPromise = uploadChunk(presignedUrl, chunk)
          .then(chunkResponse => {
            uploadedParts.push({
              ETag: chunkResponse.headers('ETag').replace(/"/g, ''),
              PartNumber: i + 1
            });

            const chunkProgress = 10 + (uploadedParts.length / presignedUrls.length) * 80;
            if (progressCallback) {
              progressCallback({
                stage: 'uploading',
                progress: Math.round(chunkProgress),
                chunksUploaded: uploadedParts.length,
                totalChunks: presignedUrls.length
              });
            }
            return chunkResponse;
          });

        chunkPromises.push(chunkPromise);
      }
      return chunkPromises;
    }

    const completeMultipartUpload = (fusionAssignmentId, uploadId, key, filename, parts, transcriptData = {}) => {
      const url = `${API_URL}/api/admin/fusion_assignment/${fusionAssignmentId}/recordings/complete-recording-upload/`;

      const payload = {
        upload_id: uploadId,
        key: key,
        filename: filename,
        parts: parts,
        transcript_content: transcriptData.transcript_content || '',
        qa_questions: transcriptData.qa_questions || '',
        qa_transcripts: transcriptData.qa_transcripts || '',
        summary: transcriptData.summary || ''
      };

      return HttpAuth.post(url, payload);
    };

    const uploadRecording = (fusionAssignmentId, file, progressCallback, existingRecordings = [], transcriptData = {}) => {
      return new Promise((resolve, reject) => {
        const validationErrors = validateFile(file);
        if (validationErrors.length > 0) {
          reject({ errors: validationErrors });
          return;
        }

        const duplicateRecording = existingRecordings.find(recording =>
          recording.recording_name === file.name && recording.active
        );

        if (duplicateRecording) {
          reject({
            isDuplicate: true,
            message: `File with name "${file.name}" already exists.`,
            existingRecording: duplicateRecording
          });
          return;
        }

        let uploadId;
        let key;
        const uploadedParts = [];

        initiateMultipartUpload(fusionAssignmentId, file)
          .then(response => {
            uploadId = response.data.upload_id;
            key = response.data.key;
            const presignedUrls = response.data.parts;

            if (progressCallback) progressCallback({ stage: 'initiated', progress: 10 });

            const chunkPromises = getChunkUploadPromises(presignedUrls, file, uploadedParts, progressCallback);

            return Promise.all(chunkPromises);
          })
          .then(() => {
            uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber);

            if (progressCallback) progressCallback({ stage: 'completing', progress: 95 });

            return completeMultipartUpload(fusionAssignmentId, uploadId, key, file.name, uploadedParts, transcriptData);
          })
          .then(response => {
            if (progressCallback) progressCallback({ stage: 'completed', progress: 100 });
            resolve(response.data.recording);
          })
          .catch(error => {
            reject(error);
          });
      });
    };

    const getRecordings = (fusionAssignmentId, includeInactive = false) => {
      const url = `${API_URL}/api/admin/fusion_assignment/${fusionAssignmentId}/recordings/`;
      const params = includeInactive ? { include_inactive: true } : {};

      return HttpAuth.get(url, { params });
    };

    const deleteRecording = (fusionAssignmentId, recordingId) => {
      const url = `${API_URL}/api/admin/fusion_assignment/${fusionAssignmentId}/recordings/${recordingId}/`;
      return HttpAuth.delete(url);
    };

    const reorderRecordings = (fusionAssignmentId, recordingIds) => {
      const url = `${API_URL}/api/admin/fusion_assignment/${fusionAssignmentId}/recordings/reorder/`;
      const payload = { recording_ids: recordingIds };

      return HttpAuth.post(url, payload);
    };


    const updateRecording = (fusionAssignmentId, recordingId, data) => {
      const url = `${API_URL}/api/admin/fusion_assignment/${fusionAssignmentId}/recordings/${recordingId}/`;
      return HttpAuth.patch(url, data);
    };

    return {
      validateFile,
      uploadRecording,
      getRecordings,
      deleteRecording,
      reorderRecordings,
      updateRecording,
      SUPPORTED_FORMATS,
      SUPPORTED_EXTENSIONS,
      SUPPORTED_MIME_TYPES,
      MAX_RECORDING_FILE_SIZE: MAX_RECORDING_FILE_SIZE / (1024 * 1024 * 1024) 
    };
  }
]);
